import { useEffect, useMemo, useState } from 'react';
import AppModal from '@/components/common/AppModal';
import ReactMarkdown from 'react-markdown';
import { SplashState } from '@/state/SplashState';
import { fetchEksportChangelog } from '@/actions/splashActions';
import { Dispatch, useDispatch, useSelector } from '@/state/store';
import { stateConfig } from '@/selectors/selectors';
import {
    CHANGELOG_MAX_RELEASES,
    splitChangelog,
    splitChangelogSections,
} from '@/utils/changelog';

interface ChangelogProps {
    handleWindowChange: (window: SplashState) => void;
}

export const Changelog = ({ handleWindowChange }: ChangelogProps) => {
    const dispatch = useDispatch();

    const [eksportChangelog, setEksportChangelog] = useState<string>('');
    const [latestChanges, setLatestChanges] = useState<string>('');
    const [oldChanges, setOldChanges] = useState<string>('');
    const [moreChangesCount, setMoreChangesCount] = useState(0);

    const [isNewExportVersion, setIsNewExportVersion] = useState<boolean>(false);

    const config = useSelector(stateConfig);
    const changelog = process.env.changelog ?? '';
    const sections = useMemo(() => splitChangelogSections(changelog), [changelog]);
    const latestSectionCount = latestChanges
        ? splitChangelogSections(latestChanges).length
        : 0;
    const firstHiddenSection = Math.max(CHANGELOG_MAX_RELEASES, latestSectionCount);
    const additionalChanges = sections
        .slice(firstHiddenSection, firstHiddenSection + moreChangesCount)
        .join('\n');
    const hasMoreChanges = sections.length > firstHiddenSection + moreChangesCount;

    useEffect(() => {
        const { latest, old } = splitChangelog(
            process.env.changelog ?? '',
            localStorage.getItem('lastVersion')
        );
        setLatestChanges(latest);
        setOldChanges(old);
    }, []);

    useEffect(() => {
        getExportChangelog(dispatch)
            .then(latest => {
                setEksportChangelog(latest);
                if (latest !== localStorage.getItem('lastExportVersion')) {
                    localStorage.setItem('lastExportVersion', latest);
                    setIsNewExportVersion(true);
                }
            })
            .catch(e => console.warn(e));
    }, [dispatch]);

    return (
        <AppModal
            show
            className="c-information-modal c-changelog"
            title="Endringslogg"
            onClose={() => handleWindowChange(SplashState.NONE)}
            headerActions={
                <button
                    type="button"
                    title="Tilbake"
                    className="c-app-modal__header-action"
                    onClick={() => handleWindowChange(SplashState.MAIN)}
                    aria-label="Gå tilbake fra endringslogg"
                    id="back_changelog_header_button"
                >
                    <i
                        className="material-icons c-app-modal__header-action-icon"
                        aria-hidden="true"
                    >
                        arrow_back
                    </i>
                </button>
            }
        >
            <main className="c-changelog-main" aria-label="Fullstendig endringslogg">
                <div className="c-changelog-latest-changes">
                    <ReactMarkdown>{latestChanges}</ReactMarkdown>
                </div>
                {latestChanges && oldChanges && <hr className="c-changelog-divider" />}
                {oldChanges || additionalChanges ? (
                    <div className="c-changelog-old-changes">
                        <ReactMarkdown>
                            {[oldChanges, additionalChanges].join('\n')}
                        </ReactMarkdown>
                    </div>
                ) : null}
                {hasMoreChanges && (
                    <button
                        type="button"
                        className="c-splash-footer-interactive__item c-changelog-load-more"
                        onClick={() =>
                            setMoreChangesCount(count => count + CHANGELOG_MAX_RELEASES)
                        }
                    >
                        Vis flere endringer
                    </button>
                )}
            </main>
            <section className="c-changelog-export" aria-label="Eksport endringslogg">
                <div
                    className={`c-changelog-${isNewExportVersion ? 'latest-changes' : 'old-changes'}`}
                >
                    <ReactMarkdown>{eksportChangelog}</ReactMarkdown>
                </div>
            </section>
            <footer className="c-changelog-footer">
                <div className="c-changelog-links">
                    <h2>Fullstendig endringslogg: </h2>
                    <a
                        className="c-splash-footer-interactive__item"
                        target="_blank"
                        rel="noreferrer noopener"
                        href={new URL('/changelog.html', config.eksport_x_url).href}
                    >
                        Eksport (nvdb-eksport)
                    </a>
                    <br />
                    <a
                        className="c-splash-footer-interactive__item"
                        target="_blank"
                        rel="noreferrer noopener"
                        href={new URL('#/changelog', config.datakatalogen_url).href}
                    >
                        Datakatalogen (dakat-innsyn)
                    </a>
                </div>
            </footer>
        </AppModal>
    );
};

async function getExportChangelog(dispatch: Dispatch): Promise<string> {
    const raw = await dispatch(fetchEksportChangelog()).unwrap();
    return `
# ${raw.version} ${raw.version !== '' ? '(Eksport)' : ''}
${raw.changes}`;
}
